// FICHIER : frontend/src/pages/Dashboard.jsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavLogo from '../components/NavLogo';
import axios from 'axios';

const API = 'https://ark-recruitment-backend.onrender.com/api';

// ══════════════════════════════════════════
// ── Icônes ──
// ══════════════════════════════════════════
const IconArrow    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>);
const IconBrand    = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>);
const IconOffre    = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>);
const IconCandidats= () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>);
const IconContenu  = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>);
const IconScore    = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>);

// ══════════════════════════════════════════
// ── Carte action rapide ──
// ══════════════════════════════════════════
function ActionCard({ icon, titre, desc, cta, onClick, actif = true, badge }) {
  return (
    <div
      onClick={actif ? onClick : undefined}
      style={{
        background: 'var(--bg3)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        cursor: actif ? 'pointer' : 'default',
        opacity: actif ? 1 : 0.5,
        transition: 'border-color 0.2s',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Badge "bientôt" */}
      {badge && (
        <div style={{ position: 'absolute', top: 12, right: 12, background: 'var(--border)', borderRadius: 20, padding: '3px 10px', fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>
          Bientôt
        </div>
      )}

      {/* Icône */}
      <div style={{ width: 40, height: 40, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent2)' }}>
        {icon}
      </div>

      {/* Texte */}
      <div>
        <div style={{ fontFamily: 'Syne,sans-serif', fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{titre}</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>{desc}</div>
      </div>

      {/* CTA */}
      {actif && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--accent)', marginTop: 'auto' }}>
          {cta} <IconArrow/>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════
// ── Composant principal Dashboard ──
// ══════════════════════════════════════════
export default function Dashboard() {
  const navigate  = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ── Détection mobile ──
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── Données réelles depuis Supabase ──
  const [stats, setStats] = useState({ score_marque: 0, niveau_marque: '—', nb_offres: 0, nb_contenus: 0, nb_candidats: 0 });

  useEffect(() => {
    const companyId = localStorage.getItem('company_id');
    if (companyId) {
      axios.get(`${API}/dashboard/${companyId}`, { headers: { 'X-Company-Id': companyId } }).then(res => setStats(res.data)).catch(() => {});
    }
  }, []);

  const scoreMarque  = stats.score_marque || 0;
  const niveauMarque = stats.niveau_marque || '—';
  const couleurScore = scoreMarque >= 80 ? 'var(--success)' : scoreMarque >= 50 ? 'var(--highlight)' : 'var(--error)';

  // ── Actions rapides ──
  const ACTIONS = [
    {
      icon:   <IconBrand/>,
      titre:  'Améliorer ma marque',
      desc:   'Retravaillez votre ADN, EVP et contenus employeur.',
      cta:    'Ouvrir le builder',
      onClick:() => navigate('/marque'),
      actif:  true,
    },
    {
      icon:   <IconOffre/>,
      titre:  'Créer une offre',
      desc:   'Générez une offre d\'emploi optimisée par l\'IA.',
      cta:    'Créer une offre',
      onClick:() => navigate('/offre-create'),
      actif:  true,
    },
    {
      icon:   <IconCandidats/>,
      titre:  'Voir les candidats',
      desc:   'Consultez les rapports d\'analyse de vos candidats.',
      cta:    'Accéder aux rapports',
      onClick:() => navigate('/recruteur'),
      actif:  true,
    },
    {
      icon:   <IconContenu/>,
      titre:  'Générer du contenu',
      desc:   'Posts LinkedIn, messages WhatsApp, annonces prêtes à publier.',
      cta:    'Générer du contenu',
      onClick:() => navigate('/contenu'),
      actif:  true,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'DM Sans,sans-serif' }}>

      {/* ── NAVIGATION ── */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '0 20px' : '0 48px', height: 64, borderBottom: '1px solid var(--border)', background: 'var(--nav-bg)' }}>
        <NavLogo/>
        <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--muted)', letterSpacing: 1 }}>
          ESPACE <span style={{ color: 'var(--accent2)' }}>ENTREPRISE</span>
        </div>
        <button
          onClick={() => { localStorage.removeItem('company_id'); localStorage.removeItem('company_nom'); localStorage.removeItem('company_email'); navigate('/'); }}
          style={{ color: 'var(--muted)', fontSize: 12, fontFamily: 'DM Mono,monospace', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: 0.5 }}
        >
          Déconnexion
        </button>
      </nav>

      {/* ── CONTENU ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '28px 16px' : '48px 32px' }}>

        {/* ── EN-TÊTE ── */}
        <div style={{ marginBottom: 36 }}>
          <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
            Tableau de bord
          </div>
          <h1 style={{ fontFamily: 'Syne,sans-serif', fontSize: isMobile ? 24 : 30, fontWeight: 800, letterSpacing: '-1px', marginBottom: 6 }}>
            Bienvenue sur votre espace entreprise
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>
            Gérez votre marque employeur, vos offres et vos candidats depuis ici.
          </p>
        </div>

        {/* ── SCORE MARQUE EMPLOYEUR ── */}
        <div style={{ background: 'var(--bg3)', border: `1px solid ${couleurScore}30`, borderRadius: 16, padding: isMobile ? '20px' : '28px 32px', marginBottom: 32, display: 'flex', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: 24 }}>

          {/* Score */}
          <div style={{ textAlign: isMobile ? 'center' : 'left', flexShrink: 0 }}>
            <div style={{ fontFamily: 'Syne,sans-serif', fontSize: 64, fontWeight: 800, color: couleurScore, lineHeight: 1 }}>{scoreMarque}</div>
            <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>Score marque employeur / 100</div>
          </div>

          {/* Séparateur vertical */}
          {!isMobile && <div style={{ width: 1, height: 80, background: 'var(--border)', flexShrink: 0 }}/>}

          {/* Infos niveau */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `${couleurScore}15`, border: `1px solid ${couleurScore}30`, borderRadius: 20, padding: '5px 14px', marginBottom: 12 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: couleurScore, display: 'inline-block' }}/>
              <span style={{ color: couleurScore, fontWeight: 700, fontSize: 12 }}>Niveau : {niveauMarque}</span>
            </div>
            <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              Votre marque employeur est en cours de structuration. Complétez votre ADN et votre EVP pour atteindre le niveau <strong style={{ color: 'var(--text)' }}>Avancé</strong>.
            </p>
          </div>

          {/* Bouton améliorer */}
          <button
            onClick={() => navigate('/marque')}
            style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 22px', fontSize: 13, fontWeight: 600, fontFamily: 'DM Sans,sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Améliorer <IconArrow/>
          </button>
        </div>

        {/* ── STATS RAPIDES ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 32 }}>
          {[
            { label: 'Score marque',   valeur: `${scoreMarque}/100`,       icon: <IconScore/>,     couleur: couleurScore },
            { label: 'Candidats',      valeur: `${stats.nb_candidats}`, icon: <IconCandidats/>, couleur: 'var(--accent2)' },
            { label: 'Offres actives', valeur: `${stats.nb_offres}`,    icon: <IconOffre/>,     couleur: 'var(--accent2)' },
            { label: 'Contenus générés',valeur: `${stats.nb_contenus}`, icon: <IconContenu/>,   couleur: 'var(--accent2)' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ color: s.couleur }}>{s.icon}</div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontSize: 22, fontWeight: 800, color: s.couleur }}>{s.valeur}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── ACTIONS RAPIDES ── */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
            Actions rapides
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 14 }}>
            {ACTIONS.map((a, i) => (
              <ActionCard key={i} {...a}/>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}